import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { UseFormReturn } from 'react-hook-form';
import { Case, Default, Else, If, Switch, Then, When } from 'react-if';
// eslint-disable-next-line no-restricted-imports -- 기존 NiceModal 기반 로딩 모달입니다. overlay-kit 전환은 별도 리팩토링에서 처리합니다.
import { useModal } from '@ebay/nice-modal-react';
import { useDidUpdate } from '@toss/react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useAuth } from '@zep/auth';
import { logger } from '@zep/core-logger';
import { useDialog } from '@zep/dialog';
import { Button } from '@zep/ui';
import { Loading } from '../../components/service/common/Loading';
import UploadBottomButtonsLayer from '../../components/service/stores/StoreUpload/UploadBottomButtonsLayer';
import UploadCommonAppForm from '../../components/service/stores/StoreUpload/UploadCommonAppForm';
import UploadCommonMapForm from '../../components/service/stores/StoreUpload/UploadCommonMapForm';
import UploadCommonObjectForm from '../../components/service/stores/StoreUpload/UploadCommonObjectForm';
import UploadDetailForm from '../../components/service/stores/StoreUpload/UploadDetailForm';
import UploadHeader from '../../components/service/stores/StoreUpload/UploadHeader';
import UploadPriceSet from '../../components/service/stores/StoreUpload/UploadPriceSet';
import { ErrorStatus, StoreCategoryString, StoreReviewState } from '../../consts';
import { useStoreAppForm, useStoreAssetForm } from '../../hooks/useStoreForms';
import { useRemoveStoreItem, useStoreEditItemDetail, useUploadStoreItem } from '../../services';
import { StoreAppUploadDataType, StoreAssetUploadDataType, StoreItemType, StoreUploadLocalizeData } from '../../types/StoreTypes';
const initialAssetUploadForm: StoreAssetUploadDataType = {
  assetHashId: '',
  isFree: true,
  price: 0,
  objectImageKeyList: [],
  sourceSpaceHashId: '',
  localizeDataSets: {}
};
const initialAppUploadForm: StoreAppUploadDataType = {
  assetHashId: '',
  isFree: true,
  price: 0,
  objectImageKeyList: [],
  sourceAppHashId: '',
  iconImageKey: '',
  localizeDataSets: {},
  subCategory: 0
};
export const StoreUploadPageView = () => {
  const dialog = useDialog();
  const {
    t
  } = useTranslation(['store']);
  const loadingSpinner = useModal(Loading);
  const {
    user
  } = useAuth();
  const router = useRouter();
  const [localeForms, setLocaleForms] = useState<Record<string, UseFormReturn<StoreUploadLocalizeData>>>({});
  const [isEditBlocked, setIsEditBlocked] = useState(false);
  const [isLocaleFormDirty, setIsLocaleFormDirty] = useState(false);
  const [inputStep, setInputStep] = useState<'common' | 'detail'>('common');
  const isAllowForceRouteChange = useRef(false);
  const uploadType: StoreItemType = (typeof router.query.type === 'string' ? router.query.type : StoreCategoryString.map) as StoreItemType;
  const isAssetType = uploadType === StoreCategoryString.map || uploadType === StoreCategoryString.object;
  const editHashId = router.query.edit?.toString() ?? '';
  const assetForm = useStoreAssetForm(uploadType, initialAssetUploadForm);
  const appForm = useStoreAppForm(uploadType, initialAppUploadForm);
  const commonForm: UseFormReturn<StoreAssetUploadDataType | StoreAppUploadDataType> = isAssetType ? assetForm : appForm;
  const {
    reset,
    trigger,
    setValue,
    formState: {
      isDirty,
      isValid
    }
  } = commonForm;

  /**
   * 에셋을 수정하려는 경우 데이터를 가져오는 API를 호출하는 Query. 추가하는 경우에는 작동하지 않는다
   */
  const assetDetails = useStoreEditItemDetail(editHashId, {
    enabled: !!editHashId
  });

  /**
   * 에셋 추가/수정을 위한 Mutation
   */
  const uploadMutation = useUploadStoreItem(uploadType, editHashId, !!user?.isEdu, {
    onMutate: () => {
      // A mutation is about to happen!
      loadingSpinner.show({
        text: 'UPLOADING'
      });
    },
    onSuccess: data => {
      if (data.status >= 0) {
        handleForceRouteChange(`/store/details?id=${data.item.hashId}`);
        logger.track('store_upload_complete', {
          type: uploadType,
          price: data.item.price
        });
      }
    },
    onError: error => {
      let errorMessage = '';
      switch (error.status) {
        case ErrorStatus.IS_NOT_IMAGE_FILE:
          // 이미지 포맷이 잘못된 경우(gif 등)
          errorMessage = t('store:store_alert_wrong_image_desc');
          break;
        case ErrorStatus.NOT_UPLOAD_PREMIUM_SPACE_MAP_ASSET:
          // 프리미엄 스페이스를 등록하려 한 경우
          errorMessage = t('store:store_alert_premium_space_desc');
          break;
        case ErrorStatus.NOT_UPLOAD_INVISIBLE_MAP_ASSET:
          // 비공개 스페이스를 등록하려 한 경우
          errorMessage = t('store:store_alert_private_space_desc');
          break;
        case ErrorStatus.NOT_UPLOAD_REVIEW_COMPLETED_MAP_ASSET:
        case ErrorStatus.NOT_UPLOAD_REVIEW_COMPLETED_APP_ASSET:
          // 이미 심사가 완료된 맵이나 앱인 경우
          errorMessage = t('store:store_alert_already_registered_desc');
          break;
        default:
          errorMessage = error.toString();
      }
      dialog.alert({
        title: errorMessage
      });
      console.error(error);
    },
    onSettled: () => {
      // Error or success... doesn't matter!
      loadingSpinner.remove();
    }
  });

  /**
   * 에셋 삭제를 위한 Mutation
   */
  const removeMutation = useRemoveStoreItem(editHashId, !!user?.isEdu, {
    onSuccess: result => {
      if (result.isDeleted) {
        handleForceRouteChange('/store');
      }
    },
    onError: (error: Error) => {
      dialog.alert({
        title: error.message
      });
    }
  });

  /**
   * 동록 또는 수정하기
   */
  const handleUpdate = async () => {
    const localizeDataSets: Record<string, StoreUploadLocalizeData> = {};

    // 모든 폼의 유효성 체크
    if (!isValid || !Object.values(localeForms).every(value => value.formState.isValid)) {
      return dialog.alert({
        title: t('store:store_alert_invalid_form_data')
      }).then(() => {
        for (const locale in localeForms) {
          localeForms[locale].trigger();
        }
      });
    }

    // 모든 폼의 데이터를 commonForm에 병합
    for (const locale in localeForms) {
      localizeDataSets[locale] = localeForms[locale].getValues();
    }
    setValue('localizeDataSets', localizeDataSets);
    setValue('assetHashId', editHashId);
    const data = commonForm.getValues();
    uploadMutation.mutate({
      ...data
    });
  };

  /**
   * 에셋을 삭제하는 API 호출
   */
  const handleRemove = () => {
    if (editHashId) {
      dialog.confirm({
        title: t('store:store_delete_confirm'),
        confirmVariant: 'alert',
        confirmAction: async () => removeMutation.mutate()
      });
    }
  };

  /**
   * 공통정보 다시 보기
   */
  const showCommonStep = async () => {
    // 상세정보 입력 폼에서 입력한 기록이 있으면 유저에게 기존 정보가 지워져도 되는지 확인
    const isAllowNavigation = isLocaleFormDirty && (await dialog.confirm({
      title: t('store:store_route_change_modal_content'),
      confirmVariant: 'alert',
      confirmText: t('store:store_route_change_modal_exit')
    })) || false;

    // 입력이 일어난 적이 업거나 유저가 허용한 경우 전환
    if (!isLocaleFormDirty || isAllowNavigation) {
      setInputStep('common');
      setIsLocaleFormDirty(false);
    }
  };

  /**
   * 상세정보 보기로 이동
   */
  const showDetailStep = () => {
    trigger(); // 폼의 유효성을 체크해서 유효하지 않은 경우 폼 하단에 에러 메시지 노출

    if (isValid) {
      setInputStep('detail');
    } else {
      dialog.alert({
        title: t('store:store_alert_invalid_form_data')
      });
    }
  };

  /**
   * 등록 시 언어별 폼의 내용을 병합하기 위해 하위 컴포넌트의 폼 객체를 셋
   * @param localeForms
   * @param isLocaleFormDirty
   */
  const setLocaleFormsState = (nextLocaleForms: Record<string, UseFormReturn<StoreUploadLocalizeData>>, nextIsLocaleFormDirty: boolean) => {
    setLocaleForms(nextLocaleForms);
    setIsLocaleFormDirty(nextIsLocaleFormDirty);
  };

  /**
   * 업로드 페이지는 Route Change 이벤트에서 차단하는 로직이 있어 강제로 이동시키는 함수
   * @param path 이동할 경로
   */
  const handleForceRouteChange = useCallback((path: string) => {
    isAllowForceRouteChange.current = true;
    router.push(path);
  }, [router]);

  /**
   * 라우팅이 변경되기 전에 호출되는 함수
   * @description useCallback을 사용하지 않으면 isDirty의 값의 변화를 감지하지 못한다.
   */
  const handleRouteChangeStart = useCallback((url: string) => {
    if (isDirty && !isAllowForceRouteChange.current) {
      dialog.confirm({
        title: t('store:store_route_change_modal_content'),
        confirmVariant: 'alert',
        confirmText: t('store:store_route_change_modal_exit'),
        confirmAction: async () => handleForceRouteChange(url)
      });

      // 이동을 취소하려면 강제로 routeChangeError를 발생시키고 throw 해야한다.
      // See https://github.com/zeit/next.js/issues/2476
      router.events.emit('routeChangeError');
      throw 'routeChange aborted. This error can be safely ignored - https://github.com/zeit/next.js/issues/2476.';
    }
  }, [isDirty, t, dialog, router, handleForceRouteChange]);
  useDidUpdate(() => {
    // 다른 타입의 에셋을 업로드 하도록 페이지 이동을 한 경우, 모든 정보 초기화
    reset(isAssetType ? initialAssetUploadForm : initialAppUploadForm, {
      keepDirty: false
    });
    setInputStep('common');
    setIsEditBlocked(!!editHashId);
  }, [router.query, editHashId]);
  useEffect(() => {
    if (assetDetails.isSuccess) {
      reset({
        assetHashId: assetDetails.data.item.hashId,
        localizeDataSets: assetDetails.data.item.localizeDataSets,
        price: assetDetails.data.item.price,
        isFree: assetDetails.data.item.price === 0,
        objectImageKeyList: assetDetails.data.item.objectImageKeyList ?? [],
        sourceSpaceHashId: assetDetails.data.item.sourceSpaceHashId ?? '',
        sourceAppHashId: assetDetails.data.item.sourceAppHashId ?? '',
        subCategory: assetDetails.data.item.subCategory ?? 0,
        iconImageKey: assetDetails.data.item.iconImageKey ?? ''
      } as StoreAssetUploadDataType);
      // 편집하려는 에셋이 심사가 완료된/심사중인 경우에는 이름만 수정가능하도록
      setIsEditBlocked([StoreReviewState.completed, StoreReviewState.required, StoreReviewState.official].includes(assetDetails.data.item.state));
    }
  }, [assetDetails.data, assetDetails.isSuccess, reset]);

  // handleRouteChangeStart가 바뀔때마다 이벤트를 다시 만들어줘야하기 때문에 useEffect에서 이벤트 해제후 다시 실행한다.
  useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChangeStart);
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [handleRouteChangeStart, router.events]);
  return <>
      <UploadHeader uploadType={uploadType} inputStep={inputStep} data-sentry-element="UploadHeader" data-sentry-source-file="upload.tsx" />

      <If condition={inputStep === 'common'} data-sentry-element="If" data-sentry-source-file="upload.tsx">
        {/* 공통정보 입력 단계 */}
        <Then data-sentry-element="Then" data-sentry-source-file="upload.tsx">
          <Switch data-sentry-element="Switch" data-sentry-source-file="upload.tsx">
            <Case condition={uploadType === StoreCategoryString.map} data-sentry-element="Case" data-sentry-source-file="upload.tsx">
              <UploadCommonMapForm commonForm={commonForm} isEditBlocked={isEditBlocked} data-sentry-element="UploadCommonMapForm" data-sentry-source-file="upload.tsx" />
            </Case>
            <Case condition={uploadType === StoreCategoryString.object} data-sentry-element="Case" data-sentry-source-file="upload.tsx">
              <UploadCommonObjectForm commonForm={commonForm} isEditBlocked={isEditBlocked} data-sentry-element="UploadCommonObjectForm" data-sentry-source-file="upload.tsx" />
            </Case>
            <Default data-sentry-element="Default" data-sentry-source-file="upload.tsx">
              <UploadCommonAppForm commonForm={commonForm} isEditBlocked={isEditBlocked} data-sentry-element="UploadCommonAppForm" data-sentry-source-file="upload.tsx" />
            </Default>
          </Switch>
          <When condition={!user?.isEdu} data-sentry-element="When" data-sentry-source-file="upload.tsx">
            <UploadPriceSet isEditable={isEditBlocked} commonForm={commonForm} data-sentry-element="UploadPriceSet" data-sentry-source-file="upload.tsx" />
          </When>
          <When condition={!!editHashId} data-sentry-element="When" data-sentry-source-file="upload.tsx">
            <Button variant="alert" className="w-[110px]" onClick={handleRemove} data-sentry-element="Button" data-sentry-source-file="upload.tsx">
              {t('store:store_asset_delete')}
            </Button>
          </When>
        </Then>
        <Else data-sentry-element="Else" data-sentry-source-file="upload.tsx">
          {/* 상세정보 입력 단계 */}
          <UploadDetailForm isEditBlocked={isEditBlocked} commonForm={commonForm} onChangeLocaleForms={setLocaleFormsState} data-sentry-element="UploadDetailForm" data-sentry-source-file="upload.tsx" />
        </Else>
      </If>

      {/* 하단 Fixed 버튼 */}
      {createPortal(<UploadBottomButtonsLayer inputStep={inputStep} editHashId={editHashId} onPrev={showCommonStep} onNext={showDetailStep} onSubmit={handleUpdate} />,
    // @ts-ignore
    document.getElementById('store-sublayout'))}
    </>;
};